// src/components/Costplanner/MaterialFinishScreen.jsx
import React, { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MaterialContext } from "../../contexts/MaterialContext";
import { usePOs } from "../../contexts/POContext";
import MaterialModal from "../Modals/MaterialModal";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useStorage } from "../../contexts/StorageContext";

const btnPrimary =
  "px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black rounded text-sm";
const tableClass =
  "min-w-full bg-[var(--color-surface)] rounded-lg overflow-hidden";

export default function MaterialFinishScreen() {
  const navigate = useNavigate();

  // Workspace I/O (optional mirroring; MaterialContext remains the source of truth in-app)
  const { hasFolder, readDomain, writeDomain, masterReceiveFiles, masterSaveFiles } = useStorage();

  const {
    materials = [],
    addMaterial,
    updateMaterial,
    deleteMaterial,
    isLoading = false,
  } = useContext(MaterialContext);

  const { orders = [], addOrder } = usePOs();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: "", category: "", supplier: "" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [toastMessage, setToastMessage] = useState(null);

  // debug banner visibility
  const [showBanner, setShowBanner] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowBanner(false), 4000);
    return () => clearTimeout(t);
  }, []);

  // Normalize derived display fields (unitPrice, unit)
  const displayRows = useMemo(() => {
    return (materials || []).map((m) => {
      const purchaseCost = Number(m.purchaseCost) || 0;
      const conversionAmount = Number(m.conversionAmount) || 1;
      const unitPrice =
        conversionAmount > 0 ? purchaseCost / conversionAmount : purchaseCost;
      const unit = m.trackingUnit || m.unit || "";
      return { ...m, unitPrice, unit };
    });
  }, [materials]);

  const filtered = useMemo(() => {
    const term = (filters.search || "").toLowerCase();
    const sup = (filters.supplier || "").toLowerCase();
    return displayRows.filter((m) => {
      const nameHit = !term || (m.name || "").toLowerCase().includes(term);
      const catHit = !filters.category || m.category === filters.category;
      const supHit =
        !sup ||
        (m.supplierName || m.supplier || "")
          .toString()
          .toLowerCase()
          .includes(sup);
      return nameHit && catHit && supHit;
    });
  }, [displayRows, filters]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));

  const categoryOptions = useMemo(
    () =>
      [...new Set((materials || []).map((m) => m.category).filter(Boolean))],
    [materials]
  );

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (m) => { setEditing(m); setModalOpen(true); };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const deleteSelected = () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} materials?`)) return;
    selectedIds.forEach((id) => deleteMaterial(id));
    setSelectedIds([]);
  };

  const exportCSV = () => {
    const csv = Papa.unparse(
      (materials || []).map((m) => {
        const purchaseCost = Number(m.purchaseCost) || 0;
        const conversionAmount = Number(m.conversionAmount) || 1;
        const unitPrice =
          conversionAmount > 0 ? purchaseCost / conversionAmount : purchaseCost;
        return {
          id: m.id,
          sku: m.sku,
          name: m.name,
          unitPrice,
          unit: m.trackingUnit || "",
          category: m.category,
          supplier: m.supplierName || m.supplier || "",
          supplierWebsite: m.website || "",
          quantity: m.startQuantity,
          lowStockLimit: m.lowStockLimit,
          inventoriable: !!m.inventoriable,
        };
      })
    );
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "materials.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["SKU", "Name", "Price", "Unit", "Category", "Supplier"]],
      body: (displayRows || []).map((m) => [
        m.sku || "",
        m.name || "",
        (Number(m.unitPrice) || 0).toFixed(4),
        m.unit || "",
        m.category || "",
        m.supplierName || m.supplier || "",
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [255, 163, 26] },
    });
    doc.save("materials.pdf");
  };

  const importCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let added = 0;
        (results.data || []).forEach((row) => {
          const unitPrice = toNum(row.unitPrice);
          const unit = row.unit || row.trackingUnit || "unit";
          const purchaseCost =
            unitPrice > 0 ? unitPrice : toNum(row.purchaseCost);
          const conversionAmount =
            unitPrice > 0 ? 1 : Math.max(1, toNum(row.conversionAmount));

          const mat = {
            id: row.id || undefined,
            sku: row.sku || "",
            name: row.name || row.Name || "Untitled",
            category: row.category || row.Category || "",
            supplier: row.supplier || row.Supplier || "",
            website: row.supplierWebsite || row.website || "",
            trackingUnit: unit,
            purchaseCost,
            conversionAmount,
            startQuantity: toNum(row.quantity ?? row.startQuantity),
            lowStockLimit: toNum(row.lowStockLimit),
            inventoriable: truthy(row.inventoriable ?? row.inventoriable?.toString()),
            image: row.image || null,
          };
          addMaterial(mat);
          added++;
        });
        alert(`Imported ${added} material(s)!`);
        e.target.value = "";
      },
    });
  };

  const onSaveFromModal = (input) => {
    if (editing?.id) {
      updateMaterial({ ...editing, ...input, id: editing.id });
    } else {
      const payload = { id: input.id || Date.now().toString(), ...input };
      addMaterial(payload);
    }
    setModalOpen(false);
    setEditing(null);
  };

  const confirmDeleteOne = (id) => {
    if (window.confirm("Delete this material? This cannot be undone.")) {
      deleteMaterial(id);
    }
  };

  // Helper functions for reorder functionality
  const getStock = (m) => {
    const stock = m?.stock ?? m?.stockQty ?? m?.startQuantity ?? 0;
    return Number.isFinite(Number(stock)) ? Number(stock) : 0;
  };

  const getStockStatus = (m) => {
    const stock = getStock(m);
    const reorderPoint = Number(m?.lowStockLimit ?? m?.reorderPoint ?? m?.minStock ?? 0);
    
    if (stock === 0) return "OUT";
    if (reorderPoint > 0 && stock <= reorderPoint) return "LOW";
    return "OK";
  };

  const showToast = (message, action = null) => {
    setToastMessage({ message, action });
    setTimeout(() => setToastMessage(null), 5000);
  };

  const handleAddToPO = (material) => {
    try {
      // Find or create a Draft PO
      let draftPO = orders.find(po => po.status === "Draft" || po.status === "draft");
      
      const stock = getStock(material);
      const reorderPoint = Number(material?.lowStockLimit ?? material?.reorderPoint ?? material?.minStock ?? 1);
      const suggestedQty = Math.max(1, reorderPoint - stock);
      
      const conv = Number(material.conversionAmount) || 1;
      const unitCost = conv > 0 ? (Number(material.purchaseCost) || 0) / conv : 0;

      const poLineItem = {
        id: `${Date.now()}-${Math.random()}`,
        materialId: material.id,
        name: material.name || "Unnamed",
        sku: material.sku || "",
        supplier: material.supplierName || material.supplier || "",
        website: material.website || material.link || "",
        unit: material.trackingUnit || material.unit || "unit",
        unitCost: unitCost,
        qty: suggestedQty,
        category: material.category || "",
        notes: `Reorder: stock ${stock}, min ${reorderPoint}`
      };

      if (draftPO) {
        // Add to existing draft PO
        const updatedItems = [...(draftPO.items || []), poLineItem];
        addOrder({
          ...draftPO,
          items: updatedItems,
          updatedAt: Date.now()
        });
      } else {
        // Create new draft PO
        draftPO = {
          id: `PO-${Date.now()}`,
          status: "Draft",
          supplier: poLineItem.supplier,
          items: [poLineItem],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          notes: "Auto-created from Materials reorder"
        };
        addOrder(draftPO);
      }

      showToast(
        `Added "${material.name}" to Draft PO`,
        {
          label: "Open PO",
          onClick: () => navigate("/dashboard/purchase-orders")
        }
      );
    } catch (error) {
      console.error("Error adding to PO:", error);
      showToast("Failed to add to PO");
    }
  };

  const allChecked =
    selectedIds.length > 0 && selectedIds.length === filtered.length;

  // ---- Optional workspace Send/Receive for materials domain (correct key) ----
  async function sendToWorkspace() {
    try {
      await writeDomain("materials_domain", materials || []);
      await masterSaveFiles();
      alert("Materials saved to workspace.");
    } catch (e) {
      alert(`Failed to save to workspace:\n${e?.message || e}`);
    }
  }

  async function receiveFromWorkspace() {
    try {
      await masterReceiveFiles({ replaceMode: true });
      const disk = await readDomain("materials_domain", []);
      const byId = new Map((materials || []).map((m) => [String(m.id), m]));
      const seen = new Set();
      (disk || []).forEach((m) => {
        seen.add(String(m.id));
        if (byId.has(String(m.id))) updateMaterial(m);
        else addMaterial(m);
      });
      (materials || []).forEach((m) => {
        if (!seen.has(String(m.id))) deleteMaterial(m.id);
      });
      alert("Materials received from workspace.");
    } catch (e) {
      alert(`Failed to receive from workspace:\n${e?.message || e}`);
    }
  }

  return (
    <div className="min-h-screen bg-neutral-900 text-white p-6 font-sans">
      {showBanner && (
        <div className="fixed top-2 right-2 bg-yellow-400 text-black px-4 py-2 rounded shadow-lg z-50">
          MaterialFinishScreen ACTIVE
        </div>
      )}

        <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          Materials & Finishes
        </h1>
        <div className="flex items-center gap-2">
          {hasFolder && (
            <>
              <button
                onClick={receiveFromWorkspace}
                className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded text-sm"
                title="Pull materials from the selected workspace and mirror locally"
              >
                Receive from Workspace
              </button>
              <button
                onClick={sendToWorkspace}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-sm"
                title="Save current materials list to the selected workspace"
              >
                Save to Workspace
              </button>
            </>
          )}
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm"
          >
            ← Dashboard
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:justify-between mb-6 space-y-4 lg:space-y-0">
        <div className="flex gap-2 flex-wrap">
          <button onClick={openAdd} className={btnPrimary}>+ Add Material</button>
          <button onClick={exportCSV} className={btnPrimary}>Export CSV</button>
          <button onClick={exportPDF} className={btnPrimary}>Export PDF</button>
          <label className="cursor-pointer bg-yellow-500 hover:bg-yellow-600 text-black rounded text-sm px-4 py-2">
            Import CSV
            <input type="file" accept=".csv" onChange={importCSV} className="hidden" />
          </label>
          {selectedIds.length > 0 && (
            <button
              onClick={deleteSelected}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
            >
              Delete {selectedIds.length}
            </button>
          )}
          <select
            value={filters.category}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
            className="p-2 bg-neutral-800 rounded text-sm"
          >
            <option value="">All Categories</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Filter by supplier…"
            value={filters.supplier}
            onChange={(e) => setFilters((f) => ({ ...f, supplier: e.target.value }))}
            className="p-2 bg-neutral-800 rounded text-sm"
          />
        </div>
        <input
          type="text"
          placeholder="Search…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          className="w-full lg:w-1/3 p-2 bg-neutral-800 rounded text-sm"
        />
      </div>

      {isLoading ? (
        <p className="italic text-white/60">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="italic text-white/60">No materials.</p>
      ) : (
        <>
          <div className="overflow-auto">
            <table className={tableClass}>
              <thead className="bg-neutral-800">
                <tr>
                  <th className="px-4 py-2 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        setSelectedIds(e.target.checked ? filtered.map((m) => m.id) : [])
                      }
                      checked={allChecked}
                    />
                  </th>
                  {["Img", "SKU", "Name", "Price", "Unit", "Cat", "Supplier", "Reorder", "Actions"].map((h, i) => (
                    <th key={i} className="px-4 py-2 text-left text-sm font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((mat) => (
                  <tr key={mat.id} className="border-b border-neutral-700 hover:bg-neutral-800">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(mat.id)}
                        onChange={() => toggleSelect(mat.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {mat.image ? (
                        <img
                          src={typeof mat.image === "string" ? mat.image : URL.createObjectURL(mat.image)}
                          alt=""
                          className="h-10 w-10 rounded object-cover"
                          onLoad={(e) => {
                            if (typeof mat.image !== "string") {
                              const url = e.currentTarget.src;
                              requestIdleCallback?.(() => URL.revokeObjectURL(url));
                            }
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-neutral-700 rounded" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{mat.sku || ""}</td>
                    <td className="px-4 py-3 text-sm">{mat.name || ""}</td>
                    <td className="px-4 py-3 text-sm">{(Number(mat.unitPrice) || 0).toFixed(4)}</td>
                    <td className="px-4 py-3 text-sm">{mat.unit || ""}</td>
                    <td className="px-4 py-3 text-sm">{mat.category || "—"}</td>
                    <td className="px-4 py-3 text-sm">{mat.supplierName || mat.supplier || "—"}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const status = getStockStatus(mat);
                        const needsReorder = status === "LOW" || status === "OUT";
                        
                        return (
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block text-[10px] leading-none px-2 py-1 rounded-full border font-medium ${
                                status === "OUT"
                                  ? "border-red-400/50 bg-red-500/20 text-red-300"
                                  : status === "LOW"
                                  ? "border-amber-400/50 bg-amber-500/20 text-amber-300"
                                  : "border-gray-500/50 bg-gray-700/30 text-gray-400"
                              }`}
                            >
                              {status}
                            </span>
                            
                            {needsReorder && (
                              <button
                                onClick={() => handleAddToPO(mat)}
                                className="px-2 py-1 rounded text-[10px] font-medium
                                  border border-blue-400/40 bg-blue-500/20 text-blue-300
                                  hover:bg-blue-500/30 hover:border-blue-400/60
                                  transition-colors"
                                title="Add to Purchase Order"
                              >
                                Add to PO
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <button
                        onClick={() => openEdit(mat)}
                        className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => confirmDeleteOne(mat.id)}
                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-2">
              <label className="text-sm">Rows per page:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(+e.target.value);
                  setCurrentPage(1);
                }}
                className="bg-neutral-800 text-white rounded px-2 py-1 text-sm"
              >
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded text-sm ${
                  currentPage === 1
                    ? "bg-neutral-700 text-neutral-400"
                    : "bg-gray-600 text-white hover:bg-gray-700"
                }`}
              >
                Prev
              </button>
              <span className="text-sm">{currentPage}/{pageCount}</span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
                disabled={currentPage === pageCount}
                className={`px-3 py-1 rounded text-sm ${
                  currentPage === pageCount
                    ? "bg-neutral-700 text-neutral-400"
                    : "bg-gray-600 text-white hover:bg-gray-700"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {modalOpen && (
        <MaterialModal
          initialMaterial={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={onSaveFromModal}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5">
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl
            bg-zinc-800 border border-white/20 text-white">
            <span className="text-sm">{toastMessage.message}</span>
            {toastMessage.action && (
              <button
                onClick={toastMessage.action.onClick}
                className="px-3 py-1 rounded text-xs font-medium
                  bg-blue-500 text-white hover:bg-blue-600
                  transition-colors"
              >
                {toastMessage.action.label}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* helpers */
function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function truthy(v) {
  if (typeof v === "boolean") return v;
  const s = String(v).toLowerCase().trim();
  return ["true", "yes", "y", "1"].includes(s);
}
